import { alpha } from '@mui/material/styles';
import type { ChromeStyles, LandingTokens } from '../types';
import { BREAKPOINTS, down } from './breakpoints';

// The bar keeps the same room around any logo; on a phone the logo shrinks to about three quarters.
const BAR_PADDING = 34;
const MOBILE_BAR_PADDING = 32;
const MOBILE_LOGO_SCALE = 0.74;

export const chromeStyles = (t: LandingTokens): ChromeStyles => ({
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 40,
    backgroundColor: alpha(t.paper, 0.82),
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    borderBottom: `1px solid ${t.lineSoft}`,
  },
  headerInner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  // A very wide logo stops at this cap and scales down inside it rather than pushing the menu
  // off the bar. The cap sits on the link because the page's own `img` rule owns the image's.
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: 'inherit',
    minWidth: 0,
    maxWidth: 'min(340px, 45cqi)',
  },
  logoImage: {
    width: 'auto',
    objectFit: 'contain',
    objectPosition: 'left center',
    transition: 'height .2s ease',
  },
  headerSize: (logoHeight) => {
    const mobile = Math.round(logoHeight * MOBILE_LOGO_SCALE);
    return {
      inner: {
        height: logoHeight + BAR_PADDING,
        [down(BREAKPOINTS.mobile)]: { height: mobile + MOBILE_BAR_PADDING },
      },
      logo: { height: logoHeight, [down(BREAKPOINTS.mobile)]: { height: mobile } },
    };
  },
  logoMark: {
    width: 38,
    height: 38,
    borderRadius: '12px',
    display: 'grid',
    placeItems: 'center',
    background: `linear-gradient(135deg, ${t.brand}, ${t.brandInk})`,
    color: '#fff',
    '& svg': { width: 20, height: 20 },
  },
  logoName: {
    fontWeight: 700,
    letterSpacing: '.16em',
    fontSize: 15,
    textTransform: 'uppercase',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: '26px',
    fontSize: 14,
    fontWeight: 500,
    color: t.muted,
  },
  // Inherits the menu's colour, so a header text colour set on the menu reaches every link.
  navLink: {
    color: 'inherit',
    '&:hover': { color: t.ink },
    [down(BREAKPOINTS.navLinks)]: { display: 'none' },
  },
  footer: {
    backgroundColor: '#0b1116',
    color: alpha(t.onDark, 0.62),
    fontSize: '13.5px',
  },
  footerContact: {
    display: 'flex',
    gap: '26px',
    flexWrap: 'wrap',
    padding: '44px 0 30px',
    [down(BREAKPOINTS.mobile)]: {
      flexDirection: 'column',
      gap: '14px',
      padding: '32px 0 24px',
    },
  },
  // Nowrap keeps a link whole: a phone or address split across lines reads as two items.
  footerLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
    color: alpha(t.onDark, 0.78),
    '&:hover': { color: '#fff' },
  },
  footerLinkIcon: { width: 15, height: 15, flex: 'none', color: t.brand },
  // A darker sill under the contact band, so the second line reads as deliberate.
  footerBar: {
    backgroundColor: '#05080a',
    borderTop: `1px solid ${alpha(t.onDark, 0.06)}`,
  },
  footerLegal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '18px',
    flexWrap: 'wrap',
    textAlign: 'center',
    padding: '18px 0',
  },
  powered: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: 12,
    letterSpacing: '.04em',
    color: alpha(t.onDark, 0.4),
    '& b': { color: alpha(t.onDark, 0.7), fontWeight: 600 },
    '& svg': { width: 13, height: 13 },
  },
});
